<template>
  <div>
    <div class="mb-2">
      <h3 class="mb-1">
        <v-icon size="small" class="mr-2">mdi-truck-trailer</v-icon>
        {{ assetContainer.code }}
      </h3>
      <v-chip size="x-small" variant="tonal" class="text-capitalize">
        <strong>{{ getTranslatedAssetContainerType(assetContainer.type) }}</strong>
      </v-chip>
    </div>

    <div>
      <yard-view-asset-container-status-select
        :container-status="assetContainerStatus"
        :asset-container="assetContainer"
        @input="updateStatus" />

      <yard-view-asset-container-state-select
        :container-state="assetContainer.state"
        entry-point="Grid View"
        :asset-container="assetContainer"
        @input="value => updateState(assetContainer.id, value)" />
    </div>

    <p class="d-flex flex-column my-4">
      <strong class="font-size-x-small">
        <v-icon class="mr-1" size="small">mdi-map-marker-outline</v-icon>
        {{ t('yard.headers.arrivedAt') }}
      </strong>
      <span>
        {{ arrivedTimeFormatted }}
      </span>
    </p>

    <p class="d-flex flex-column my-4">
      <strong class="font-size-x-small">
        <v-icon class="mr-1" size="small">mdi-calendar</v-icon>
        {{ t('yard.headers.daysAtYard') }}
      </strong>
      <span>
        {{ daysAtYard }}
      </span>
    </p>

    <p class="d-flex flex-column my-4"></p>
    <div class="d-flex flex-column font-size-x-small mb-4" v-if="showAssignSpotSelect">
      <strong class="mb-2">{{ t('yard.headers.assignTo') }}</strong>
      <yard-view-spot-select @input="$emit('assign', $event)" asset-type="assetContainer" />
    </div>

    <v-expansion-panels flat class="asset-details-data-panels" multiple v-if="assetContainer">
      <v-expansion-panel>
        <v-expansion-panel-title class="pl-1 py-2">
          <p>{{ t('yard.headers.dropoffCarrier') }}</p>
        </v-expansion-panel-title>
        <v-expansion-panel-text class="pl-1">
          <yard-view-asset-container-appointment-data
            :appointment="dropAppointment"
            :asset-container="assetContainer"
            appointment-type="drop" />
        </v-expansion-panel-text>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-title class="pl-1 py-2">
          <p>{{ t('yard.headers.pickupCarrier') }}</p>
        </v-expansion-panel-title>
        <v-expansion-panel-text class="pl-1">
          <yard-view-asset-container-appointment-data
            :appointment="pickupAppointment"
            :asset-container="assetContainer"
            appointment-type="pickup" />
        </v-expansion-panel-text>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-title class="pl-1 py-2">
          <p>{{ t('yard.pages.yardView.headers.assetDetails') }}</p>
        </v-expansion-panel-title>
        <v-expansion-panel-text class="pl-1">
          <yard-view-asset-container-form-data
            :key="assetContainer.id"
            :asset-container="assetContainer"
            :warehouse="selectedWarehouse"
            :triggers="triggers"
            is-editable />
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
import { computed, defineComponent } from 'vue';
import useYard from '@/modules/yard/composables/useYard';

import { LuxonDateTimeFormats } from '@nova/core';
import YardViewSpotSelect from '@/modules/yard/components/Selects/YardViewSpotSelect.vue';
import YardViewAssetContainerAppointmentData from '@/modules/yard/components/AssetContainer/YardViewAssetContainerAppointmentData.vue';
import YardViewAssetContainerFormData from '@/modules/yard/components/AssetContainer/YardViewAssetContainerFormData.vue';
import YardViewAssetContainerStatusSelect from '@/modules/yard/components/Selects/YardViewAssetContainerStatusSelect.vue';
import YardViewAssetContainerStateSelect from '@/modules/yard/components/Selects/YardViewAssetContainerStateSelect.vue';
import { useEventHub } from '@/composables';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'YardViewAssetContainerDetailData',
  components: {
    YardViewSpotSelect,
    YardViewAssetContainerAppointmentData,
    YardViewAssetContainerFormData,
    YardViewAssetContainerStatusSelect,
    YardViewAssetContainerStateSelect
  },
  emits: ['assign'],
  props: {
    assetContainer: {
      type: Object,
      required: true
    },
    showAssignSpotSelect: {
      type: Boolean,
      default: true
    }
  },
  setup(props) {
    const { t } = useI18n();
    const yardStore = useYard();
    const eventHub = useEventHub();

    const pickupAppointment = computed(() => {
      return props.assetContainer?.pickupAppointment ?? null;
    });

    const dropAppointment = computed(() => {
      return props.assetContainer?.dropAppointment ?? null;
    });

    const arrivedTimeFormatted = computed(() => {
      return yardStore.formatDateTime(
        props.assetContainer?.createDateTime,
        LuxonDateTimeFormats.ShortDayDateSlashed12Hour,
        LuxonDateTimeFormats.ShortDayDateSlashed24Hour
      );
    });

    const daysAtYard = computed(() =>
      yardStore.relativeTimeFromWarehouse(props.assetContainer?.createDateTime)
    );

    const assetContainerStatus = computed(() =>
      yardStore.getAssignedAssetContainerStatus(props.assetContainer?.assetContainerEvents || [])
    );

    const updateStatus = eventType => {
      return yardStore.updateAssetContainerStatus(props.assetContainer.id, eventType);
    };

    const updateState = async (assetContainerId, state) =>
      yardStore
        .updateAssetContainer(assetContainerId, { state })
        .then(assetContainer => eventHub.$emit('update-AssetContainer', assetContainer));

    return {
      t,
      updateStatus,
      updateState,
      assetContainerStatus,
      pickupAppointment,
      triggers: yardStore.assetContainerTriggersRef,
      dropAppointment,
      daysAtYard,
      selectedWarehouse: yardStore.selectedWarehouseRef,
      arrivedTimeFormatted,
      getAssetContainerStateIcon: yardStore.getAssetContainerStateIcon,
      getTranslatedAssetContainerType: yardStore.getTranslatedAssetContainerType
    };
  }
});
</script>
<style lang="scss">
.asset-details-data-panels {
  .v-expansion-panel-text__wrapper {
    padding: 0 !important;
  }
}
</style>
